/*
    Container for displaying the Starter Sheet Report,
    which can either be a list of records or else an aggregated summary,
    there is also a filter for choosing which records to display

    the records can be edited in place and then submitted en masse

    the records can be displayed in two modes:
    time: lane, departure time, etc
    passengers: counts of passengers arriving, boarding, etc

    we use AutoSizer + FixedSizeList to ensure that only the 
    records which are currently on screen get rendered
*/

import React, { useContext } from "react";
import Media from "react-media";

import MainContext from "../MainContext";
import DataContext from "../DataContext";

import VerticalLayout from "../Components/VerticalLayout";
import SubNav from "../Components/SubNav";

import ReportStarterFilter from "../Filters/ReportStarterFilter";
import ReportStarterData from "../DataDisplay/ReportStarterData";
import StarterSheetSummary from "../DataDisplay/StarterSheetSummary";

import { getStarterSheetReport, submitEditedStarterEntry, getStarterSummary, getStarterSheetPDF } from "../Requests/ReportRequests";

import ReportExport from "../Components/Excel/ReportExport";

// tablet & laptop data height: 43px

import { FixedSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";

//
// wrapper component to make sure that we can immediately reload the data if that value is set within the DataContext
//
const ReportStarterPageContainer = (props) => {
    const context = useContext(DataContext);

    return (
        <ReportStarterPage {...props}
            dataReload={context.dataReload}
            setReload={context.setReload}
        />
    )
};

export default ReportStarterPageContainer;

class ReportStarterPage extends React.Component {

    static contextType = MainContext;

    state = {
        filterValues: null,
        data: [],
        editedIndexes: [],
        mode: "Time",
        display: "Main",
        summary: {},
        extraToggle: false,
        showLegend: false
    }

    //
    //
    getData = (callback) => {
        console.log("GET STARTER REPORT FILTER PARAMS", this.state.filterValues);

        // just fire this off on its own since it can run independently
        this.getSummaryData();

        const request = () => getStarterSheetReport(this.state.filterValues);

        const then = ({data}) => {

            console.log("STARTER REPORT DATA", data);

            const message = (data.length > 0) ? "" : "No Records Found for this Filter";

            this.setState({data, message, isEdited: false}, () => {
                if(callback && typeof callback === "function"){
                    callback();
                }
            });
        };

        this.context.runProcessing(request, then, "getStarterSheetReport");
    }

    //
    //
    getSummaryData = () => {
        const request = () => getStarterSummary(this.state.filterValues);
        const then = ({data}) => {
            console.log("STARTER SUMMARY DATA", data);
            this.setState({summary: data});
        };

        this.context.runProcessing(request, then, "getStarterSummary");
    }

    //
    //
    runPDF = () => {
        const request = () => getStarterSheetPDF(this.state.filterValues);
        const then = (res => {
            ;
        });
        this.context.runProcessing(request, then, "getStarterSheetPDF");
    }

    //
    //
    applyFilter = (values, callback) => {
        this.context.toggleFilter();
        this.setState({filterValues: values, editedIndexes: []}, () => {
            this.getData(callback);
        });
    }

    //
    // change the value of a particular field in a record in the list,
    // make sure to correctly calculate the passenger count 
    //
    updateEntry = (field, value, index) => {
        const copy = this.state.data.map((row, i) => {
            if(index === i){
                const newRow = {
                    ...row,
                    [field]: value
                };

                const boarding = newRow.passBoarding ? parseInt(newRow.passBoarding) : 0;
                const arriving = newRow.passArrival ? parseInt(newRow.passArrival) : 0;
                const gettingOff = newRow.passGetOff ? parseInt(newRow.passGetOff) : 0;
                
                if(newRow.passBoarding === "" && newRow.passArrival === "" && newRow.passGetOff === ""){
                    newRow.passDeparture = "";
                }
                else{
                    newRow.passDeparture = arriving + boarding - gettingOff;
                }

                return newRow;
            }
            else{
                return row;
            }
        });

        if(!this.state.editedIndexes.includes(index)){
            const editedIndexes = [...this.state.editedIndexes, index];
            this.setState({data: copy, editedIndexes, extraToggle: !this.state.extraToggle});
        }
        else{
            this.setState({data: copy, extraToggle: !this.state.extraToggle});
        }
    }

    //
    // submit all of the altered records so they can be stored in the DB
    //
    submitAll = () => {

        console.log("starter submit", this.state.editedIndexes);

        const promises = [];

        // only the edited records,
        // pushed into an array of promises 
        this.state.editedIndexes.forEach((index) => {
            promises.push(submitEditedStarterEntry(this.state.data[index]));
        });

        if(promises.length > 0){
            // make sure they all complete before proceeding
            const request = () => Promise.all(promises);

            const then = (res) => {
                console.log("REPORT STARTER PAGE: successfully completed promises", res);
                if(res){
                    // success notification
                    this.setState({editedIndexes: []});
                    this.context.sendNotification(false, "Trips updated successfully!");
                }
                else{
                    // error notification
                    this.context.sendNotification(true, "Error updating the trips, please try again.");
                }
            };

            this.context.runProcessing(request, then, "submitEditedStarterEntry");
        }
    }

    //
    // reset all of the records to their original values
    //
    resetAll = () => {
        this.getData();
    }

    //
    // switch between the time and passengers data display
    //
    switchMode = () => {
        const nextMode = this.state.mode === "Time" ? "Passengers" : "Time";
        this.setState({mode: nextMode, extraToggle: !this.state.extraToggle});
    }

    //
    // switch between the report records and the summary view
    //
    switchDisplay = () => {
        const nextDisplay = this.state.display === "Main" ? "Summary" : "Main";
        this.setState({display: nextDisplay});
    }

    //
    // necessary method for using the AutoSizer + FixedSizeList 
    //
    NextRow = ({ index, style }) => (
        <div style={style}>
            <ReportStarterData key={index} index={index} data={this.state.data[index]} updateEntry={this.updateEntry} mode={this.state.mode} />
        </div>
    )

    //
    //
    componentDidUpdate() {
        if(this.props.dataReload){
            console.log("GET DATA!!!");
            this.props.setReload(false, () => {                
                this.getData();
            });
        }
    }

    //
    //
    //
    toggleLegend(val){
        if(val !== null){
            this.setState({showLegend: val});
        }
        else{
            this.setState({showLegend: !this.state.showLegend});
        }
    }

    render() {

        console.log("REPORT STARTER DATA", this.state.data);

        const firstHeight = (this.state.data.length === 0 || this.context.filterOpen) ? 0 : 14;

        const nextMode = this.state.mode === "Time" ? "Switch to Passengers" : "Switch to Time & Delay";

        const nextDisplay = this.state.display === "Main" ? "Switch to Summary" : "Switch to Main";

        const exclude = ["CleverColor", "Color", "VehicleColor"];

        const legendValues = [
            {
                content: "All departed trips and On time",
                color: "var(--soft-blue)"
            },
            {
                content: "Late trip",
                color: "red"
            },
            {
                content: "Early trip",
                color: "yellow"
            },
            {
                content: "Lost trips",
                color: "salmon"
            },
            {
                content: "No show trips",
                color: "peachpuff"
            },
            {
                content: "Reassigned departed trips",
                color: "lightgray"
            },
            {
                content: "Reassigned lost trips – (No run number)",
                color: "salmon"
            }
        ]

        return (
            <VerticalLayout firstHeight={firstHeight}>
                {
                    //
                    // if the filter isn't open, then show the mode/view switch and the export buttons
                    //
                    !this.context.filterOpen && this.state.data.length > 0
                    ?<div>
                        <SubNav>
                            {
                                this.state.filterValues.employee
                                ? <div>{this.state.filterValues.employee}</div>
                                :  null
                            }
                            {
                                this.state.display === "Main"
                                ? <div className="dangerButton" onClick={this.switchMode}>{nextMode}</div>
                                : null
                            }
                            <div className="dangerButton" onClick={this.switchDisplay}>{nextDisplay}</div>
                            {
                                this.state.editedIndexes.length > 0
                                ? <>
                                    <div className="dangerButton" style={{margin: "0 10px 0 20px"}} onClick={this.submitAll}>Submit</div>
                                    <div className="dangerButton" style={{margin: "0 20px 0 10px"}} onClick={this.resetAll}>Reset</div>
                                </>
                                : null
                            }
                            <ReportExport filename={`Starter Report ${new Date()}`} data={[{name: `Report`, records: this.state.data}]} filter={this.state.filterValues} exclude={exclude} />
                            <div className="option  " onClick={this.runPDF}>PDF</div>

                            <div style={{
                                    position: "relative",
                                    borderRadius: "50%",
                                    backgroundColor: "white",
                                    border: "2px solid black",
                                    width: "1.5rem",
                                    height: "1.5rem",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginLeft: "2rem",
                                    cursor: "pointer",
                                    padding: "0.25rem",
                                    marginBottom: "0.1rem"
                                }} 
                                onClick={() => {this.toggleLegend(true)}}
                            >
                                ?
                            </div>
                            {
                                this.state.showLegend
                                ? <div style={{
                                        position: "absolute",
                                        backgroundColor: "var(--navy)",
                                        color: "white",
                                        border: "2px solid white",
                                        zIndex: "10",
                                        top: "8vh",
                                        right: "10vw",
                                        padding: "1.5rem",
                                        borderRadius: "1rem",
                                        fontSize: "1.1rem"
                                    }}
                                >
                                    <div style={{
                                        position: "relative",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "1rem"
                                    }}>
                                        <div
                                            style={{
                                                position: "absolute",
                                                right: "0.5rem",
                                                border: "1px solid white",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                cursor: "pointer",
                                                padding: "0.1rem 0.3rem"
                                            }} 
                                            onClick={()=> {this.toggleLegend(false)}}
                                        >
                                            close
                                        </div>
                                        <div>RECORD COLOR CODING</div>
                                        {
                                            legendValues.map(l => {
                                                return (
                                                    <div style={{
                                                        display: "grid",
                                                        gridTemplateColumns: "1fr 3fr",
                                                        alignItems: "center"
                                                    }}>
                                                        <div style={{backgroundColor: l.color, width: "100%", height: "1rem", marginRight: "1rem"}}></div>
                                                        <div style={{marginLeft: "0.5rem"}}>
                                                            {l.content}
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        }
                                    </div>
                                </div>
                                : null
                            }

                        </SubNav>
                        {
                            // record table header
                            this.state.display === "Main"
                            ? <>
                                <Media query="(max-width: 811px)">
                                    <TableHeader marginStyle={"8px 8px 0 8px"} mode={this.state.mode} />
                                </Media>
                                <Media query="(min-width: 812px)">
                                    <TableHeader marginStyle={"8px 25px 0 8px"} mode={this.state.mode} />
                                </Media>
                            </>
                            : null
                        }
                    </div>
                    : null
                }
                <div className="content-nooverflow">
                    <>
                    {
                        // the filter
                        this.context.hasFilter
                        && this.context.filterOpen
                            ? <ReportStarterFilter applyFilter={this.applyFilter} filterValues={this.state.filterValues} />
                            : null
                    }
                    {
                        this.state.display === "Main"
                        ? <>
                            {
                                this.state.data.length > 0
                                ? <AutoSizer>
                                {
                                    ({height, width}) => (
                                        <FixedSizeList
                                        className="fixed-list"
                                        height={height}
                                        width={width}
                                        itemCount={this.state.data.length}
                                        itemSize={43}
                                        extraToggle={this.state.extraToggle}
                                        onRowsRendered={() => {}}
                                        >
                                        {this.NextRow}
                                        </FixedSizeList>
                                    )
                                }
                                </AutoSizer>
                                : null
                            }
                            {
                                this.state.message
                                ? <div className="message">
                                    {this.state.message}
                                </div>
                                : null
                            }
                        </>
                        : <StarterSheetSummary summary={this.state.summary} />
                    }
                    </>
                </div>
            </VerticalLayout>
        );
    }
}

//
// separate header component, for code clarity
// displays different column headers based on the mode
//
const TableHeader = (props) => {

    const col5 = props.mode === "Time" ? "Lane" : "Boarding";
    const col6 = props.mode === "Time" ? "Sched" : "Arriving";
    const col7 = props.mode === "Time" ? "Act" : "Getting Off";
    const col8 = props.mode === "Time" ? "Delay" : "Left Behind";
    const col9 = props.mode === "Time" ? "CAD" : "Departing";

    return <div className="data" style={{
        display: "grid",
        gridTemplateColumns: "3fr 2fr 2fr 2fr 2fr 2fr 3fr 2fr 2fr",
        justifyItems: "center",
        alignItems: "center",
        gridGap: "4px",
        padding: "4px 0 4px 0",
        margin: props.marginStyle,
        fontWeight: "bold",
        background: "var(--purple)"
    }}>
        <div>Date</div>
        <div>ID</div>
        <div>Run</div>
        <div>Route</div>
        <div>{col5}</div>
        <div>{col6}</div>
        <div>{col7}</div>
        <div>{col8}</div>
        <div>{col9}</div>
    </div>
}